import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormField } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import DateOfBirthField from "./DateOfBirthField";

const SOCIAL = ["google", "apple"];

const calculateAge = (dob: string) => {
  const t = new Date(), b = new Date(dob);
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a;
};

/** After a first Google/Apple sign-in, collect DOB (if missing) plus optional school & phone. */
const SocialProfileCompletion = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [needsDob, setNeedsDob] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm({ defaultValues: { dateOfBirth: "", school: "", phoneNumber: "" } });

  useEffect(() => {
    if (!user) { setOpen(false); return; }
    const provider = user.app_metadata?.provider as string | undefined;
    const providers = (user.app_metadata?.providers as string[] | undefined) ?? [];
    const isSocial = SOCIAL.includes(provider ?? "") || providers.some((p) => SOCIAL.includes(p));
    if (!isSocial || user.user_metadata?.profile_completed) return;
    (async () => {
      const { data } = await supabase.from("profiles").select("date_of_birth, school, phone_number").eq("id", user.id).maybeSingle();
      const dob = data?.date_of_birth || user.user_metadata?.birthdate || user.user_metadata?.date_of_birth;
      setNeedsDob(!dob);
      form.reset({ dateOfBirth: dob || "", school: data?.school || "", phoneNumber: data?.phone_number || "" });
      setOpen(true);
    })();
  }, [user?.id]);

  const save = async (skipOptional: boolean) => {
    if (!user) return;
    setError(null);
    const v = form.getValues();
    if (needsDob) {
      if (!v.dateOfBirth) { setError("Please enter your date of birth."); return; }
      if (calculateAge(v.dateOfBirth) < 13) {
        toast.error("You must be at least 13 years old to use AnySkill");
        await supabase.auth.signOut();
        setOpen(false);
        return;
      }
    }
    setSaving(true);
    const update: { date_of_birth?: string; school?: string | null; phone_number?: string | null } = {};
    if (needsDob) update.date_of_birth = v.dateOfBirth;
    if (!skipOptional) {
      update.school = v.school || null;
      update.phone_number = v.phoneNumber || null;
    }
    if (Object.keys(update).length) {
      const { error: e } = await supabase.from("profiles").update(update).eq("id", user.id);
      if (e) { setSaving(false); setError("Couldn't save your details. Please try again."); return; }
    }
    await supabase.auth.updateUser({ data: { profile_completed: true } });
    setSaving(false);
    setOpen(false);
    toast.success("Profile complete!");
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Finish setting up your account</DialogTitle>
          <DialogDescription>
            {needsDob ? "We need your date of birth. School and phone number are optional." : "School and phone number are optional — add them now or skip."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <div className="space-y-4">
            {needsDob && (
              <FormField control={form.control} name="dateOfBirth" render={({ field }) => (
                <DateOfBirthField value={field.value} onChange={field.onChange} />
              )} />
            )}
            <div className="space-y-2">
              <Label htmlFor="sp-school" className="text-muted-foreground">Are you a student? Add your school to connect with others like you.</Label>
              <Input id="sp-school" placeholder="e.g., Scripps Ranch High School (optional)" {...form.register("school")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sp-phone">Phone Number <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input id="sp-phone" type="tel" placeholder="e.g., (555) 123-4567" {...form.register("phoneNumber")} />
              <p className="text-xs text-muted-foreground">Add your phone number to receive important updates about payments and services</p>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" disabled={saving} onClick={() => save(true)}>Skip</Button>
              <Button type="button" className="flex-1" disabled={saving} onClick={() => save(false)}>{saving ? "Saving..." : "Continue"}</Button>
            </div>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SocialProfileCompletion;
